import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { heroStats, heroStatement, heroIntroParas } from "../data/reportData";
import { CountUp } from "./charts/Charts";
import { CircledWord } from "./CircledWord";

gsap.registerPlugin(ScrollTrigger);

const GREEN = "#2BF54C";
const STAT_GREEN = "#4CBC53";

function VortexMark({ className }) {
  const rows = [
    { cy: 2.8, rx: 6.5, ry: 1.4, cx: 28 },
    { cy: 7, rx: 12.5, ry: 1.8, cx: 27.4 },
    { cy: 11.5, rx: 18.5, ry: 2.1, cx: 28 },
    { cy: 16.2, rx: 23.5, ry: 2.4, cx: 28.3 },
    { cy: 21, rx: 26, ry: 2.6, cx: 28 },
    { cy: 25.8, rx: 22.5, ry: 2.4, cx: 27.7 },
    { cy: 30.4, rx: 17.5, ry: 2.1, cx: 28 },
    { cy: 34.8, rx: 11.5, ry: 1.8, cx: 28.4 },
    { cy: 38.8, rx: 6, ry: 1.4, cx: 28 },
  ];
  return (
    <svg viewBox="0 0 56 42" className={className} fill="#111111" aria-hidden="true" data-testid="vortex-mark">
      {rows.map((r, i) => (
        <ellipse key={i} cx={r.cx} cy={r.cy} rx={r.rx} ry={r.ry} />
      ))}
    </svg>
  );
}

export default function Hero() {
  const heroRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Parallax: headline drifts slower than scroll
      gsap.to(titleRef.current, {
        yPercent: 26,
        ease: "none",
        scrollTrigger: { trigger: ".hero-banner", start: "top top", end: "bottom top", scrub: true },
      });
      // Background vortex drifts subtly for depth
      gsap.to(".hero-atmo", {
        yPercent: -2.5,
        scale: 1.03,
        ease: "none",
        scrollTrigger: { trigger: ".hero-banner", start: "top top", end: "bottom top", scrub: true },
      });
      // Stats block entrance stagger
      gsap.from(".hero-stat-cell", {
        y: 44,
        opacity: 0,
        stagger: 0.12,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: { trigger: ".hero-stats-row", start: "top 88%" },
      });
    }, heroRef);
    return () => ctx.revert();
  }, []);

  const lineReveal = (delay) => ({
    initial: { y: "112%" },
    animate: { y: 0 },
    transition: { delay, duration: 0.95, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div id="top" ref={heroRef} className="relative" data-testid="hero-section">
      {/* ── 1. Dark-green cinematic banner: first viewport ── */}
      <div
        className="hero-banner relative flex flex-col items-center justify-center min-h-screen overflow-hidden px-4"
        style={{ background: "#030704" }}
        data-testid="hero-banner"
      >
        {/* Vortex background from brand asset */}
        <div className="hero-atmo absolute inset-[-3%]" aria-hidden="true">
          <img
            src="/brand/hero-vortex.webp"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-vortex-bg"
          />
        </div>

        {/* Centered headline */}
        <div ref={titleRef} className="relative z-10 text-center pt-10">
          <h1
            className="font-display font-medium text-white leading-[1.03] tracking-[-0.015em]"
            style={{ fontSize: "clamp(46px, 7.2vw, 142px)" }}
            data-testid="hero-title"
          >
            <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
              <motion.span className="block" {...lineReveal(0.35)}>The Age of</motion.span>
            </span>
            <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
              <motion.span className="block" {...lineReveal(0.47)}>Custom Software</motion.span>
            </span>
            <span className="block overflow-hidden pb-[0.45em] -mb-[0.45em] pt-[0.3em] -mt-[0.3em]">
              <motion.span className="block" {...lineReveal(0.59)}>
                for{" "}
                <CircledWord color={GREEN} mount delay={1.4} strokeWidth={2.6} testId="hero-circle-annotation">
                  Businesses
                </CircledWord>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mx-auto mt-5 md:mt-6 text-white/95 leading-[1.5]"
            style={{ fontSize: "clamp(15px, 1.45vw, 29px)", maxWidth: "1140px" }}
            data-testid="hero-subtitle"
          >
            Independent businesses are no longer limited to the software they can buy.
            <br className="hidden md:block" />{" "}
            They build their own, and they run on it.
          </motion.p>
        </div>
      </div>

      {/* ── Below the banner: white section, ref design ── */}
      <div className="bg-white">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-20">
          {/* ── 2. Bordered stat card: icon cell + 3 stat cells ── */}
          <section id="overview" data-testid="hero-stats">
            <div className="hero-stats-row grid md:grid-cols-[20.5%_1fr_1fr_1fr] border border-[#DBDBDB]">
              <div className="hero-stat-cell flex items-center justify-center py-10 md:py-0 md:border-r border-[#DBDBDB] max-md:border-b">
                <VortexMark className="w-[58px] h-auto" />
              </div>
              {heroStats.map((s, i) => (
                <div
                  key={s.label}
                  className={`hero-stat-cell px-6 md:px-7 py-7 md:py-8 ${i > 0 ? "md:border-l border-[#DBDBDB]" : ""} max-md:border-t max-md:border-[#DBDBDB]`}
                  data-testid={`hero-stat-${i}`}
                >
                  <div className="font-display font-semibold text-[42px] md:text-[54px] leading-none tracking-[-0.02em]" style={{ color: STAT_GREEN }}>
                    <CountUp value={s.value} suffix={s.suffix} />
                  </div>
                  <p className="font-medium text-[15px] md:text-[16px] text-[#1A1A1A] mt-5 leading-snug">{s.label}</p>
                  <div className="border-t border-[#D6D6D6] mt-4 pt-3 pr-2">
                    <p className="text-[13px] text-[#8A8A8A] leading-[1.6]">
                      {s.sub.includes("sample") ? (
                        <>
                          {s.sub.split("sample")[0]}
                          <strong className="font-bold text-[#6B6B67]">sample</strong>
                          {s.sub.split("sample")[1]}
                        </>
                      ) : (
                        s.sub
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── 3. Statement + intro copy, two columns ── */}
          <section className="mt-16 md:mt-24" data-testid="hero-intro-columns">
            <div className="grid md:grid-cols-[46%_1fr] gap-10 md:gap-16">
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="font-display font-medium text-[22px] md:text-[27px] leading-[1.35] tracking-[-0.01em] text-[#1A1A1A]"
                data-testid="hero-statement"
              >
                {heroStatement}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-xl space-y-4 md:pt-1"
              >
                {heroIntroParas.map((p, i) => (
                  <p key={i} className="text-[14px] leading-[1.8] text-[#555555]">{p}</p>
                ))}
                <p className="text-[14px] leading-[1.8] text-[#555555]">
                  See <a href="#methodology" className="underline underline-offset-2 text-[#1A1A1A] font-medium hover:text-[#2BAF3C] transition-colors">Methodology</a>.
                </p>
              </motion.div>
            </div>
          </section>

          {/* ── 4. Bridge callout: bordered split card ── */}
          <section className="mt-16 md:mt-20">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="grid md:grid-cols-[71.5%_1fr] border border-[#DBDBDB]"
              data-testid="overview-callout"
            >
              <div className="px-7 md:px-11 py-9 md:py-11">
                <p className="font-medium text-[12px] tracking-[0.22em] mb-4" style={{ color: STAT_GREEN }}>WHO BUILT THEM</p>
                <p className="font-display font-medium text-[20px] md:text-[26px] leading-[1.35] tracking-[-0.01em] text-[#1A1A1A]">
                  Almost none were built by a development team.
                  <br className="hidden md:block" />{" "}
                  They were built by the people who run the business.
                </p>
              </div>
              <div className="flex items-center justify-center md:border-l border-[#DBDBDB] max-md:border-t py-10 md:py-0">
                <VortexMark className="w-[64px] h-auto" />
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </div>
  );
}
